import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { useEffect } from "react";
import { AuthProvider } from "@/contexts/AuthContext";
import ProtectedRoute from "@/components/ProtectedRoute";
import ErrorBoundary from "@/components/ErrorBoundary";
import Layout from "@/components/Layout";
import Login from "@/pages/Login";
import Dashboard from "@/pages/Dashboard";
import ProductSelector from "@/pages/ProductSelector";
import Calculator from "@/pages/Calculator";
import StraightRampCalc from "@/pages/StraightRampCalc";
import InfillCalc from "@/pages/InfillCalc";
import StepCalc from "@/pages/StepCalc";
import QuoteHistory from "@/pages/QuoteHistory";
import QuoteDetail from "@/pages/QuoteDetail";
import AdminPricing from "@/pages/AdminPricing";
import AdminUsers from "@/pages/AdminUsers";
import PricingHealth from "@/pages/PricingHealth";
import PublicRampCalculator from "@/pages/PublicRampCalculator";
import Logout from "@/pages/Logout";
import { Toaster } from "sonner";

const TITLE_BASE = "Ezy Access Solutions";
const ROUTE_TITLES = [
  ["/ramp-calculator",                    "Ramp Calculator"],
  ["/quote-portal/login",                 "Staff Login | Quote Portal"],
  ["/quote-portal/logout",                "Signing Out | Quote Portal"],
  ["/quote-portal/admin/pricing",         "Pricing Settings | Quote Portal"],
  ["/quote-portal/admin/pricing-health",   "Pricing Health | Quote Portal"],
  ["/quote-portal/admin/users",           "User Management | Quote Portal"],
  ["/quote-portal/admin",                 "Admin | Quote Portal"],
  ["/quote-portal/quotes",                "Quote History | Quote Portal"],
  ["/quote-portal/calculators/straight-ramp", "Straight Ramp | Quote Portal"],
  ["/quote-portal/calculators/wing-ramp",     "Wing Ramp | Quote Portal"],
  ["/quote-portal/calculators/infill",        "Infill | Quote Portal"],
  ["/quote-portal/calculators/step",          "Step / Platform | Quote Portal"],
  ["/quote-portal",                       "Products | Quote Portal"],
];

function TitleWatcher() {
  const { pathname } = useLocation();
  useEffect(() => {
    const match = ROUTE_TITLES.find(([p]) => pathname.startsWith(p));
    const t = match ? match[1] : "Ramp Calculator";
    document.title = `${t} | ${TITLE_BASE}`;
    // Full white-label: hide Emergent badge on EVERY page
    document.body.classList.add("white-label");
    const badge = document.getElementById("emergent-badge");
    if (badge) badge.style.setProperty("display", "none", "important");
  }, [pathname]);
  return null;
}

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <TitleWatcher />
        <ErrorBoundary>
          <Routes>
            {/* PUBLIC */}
            <Route path="/ramp-calculator" element={<PublicRampCalculator />} />

            {/* PRIVATE QUOTE PORTAL — wrapped in its own ErrorBoundary so a portal crash
                never affects the public /ramp-calculator route */}
            <Route path="/quote-portal/login" element={<Login />} />
            <Route path="/quote-portal/logout" element={<Logout />} />
            <Route element={<ProtectedRoute><ErrorBoundary><Layout /></ErrorBoundary></ProtectedRoute>}>
              <Route path="/quote-portal" element={<ProductSelector />} />
              <Route path="/quote-portal/dashboard" element={<Dashboard />} />
              <Route path="/quote-portal/products" element={<ProductSelector />} />
              <Route path="/quote-portal/calculators/straight-ramp" element={<StraightRampCalc />} />
              <Route path="/quote-portal/calculators/wing-ramp" element={<Calculator />} />
              <Route path="/quote-portal/calculators/infill" element={<InfillCalc />} />
              <Route path="/quote-portal/calculators/step" element={<StepCalc />} />
              <Route path="/quote-portal/calculator" element={<Calculator />} />
              <Route path="/quote-portal/quotes" element={<QuoteHistory />} />
              <Route path="/quote-portal/quotes/:id" element={<QuoteDetail />} />
              <Route path="/quote-portal/admin/pricing" element={<ProtectedRoute adminOnly><AdminPricing /></ProtectedRoute>} />
              <Route path="/quote-portal/admin/pricing-health" element={<ProtectedRoute adminOnly><PricingHealth /></ProtectedRoute>} />
              <Route path="/quote-portal/admin/users" element={<ProtectedRoute moderatorOrAbove><AdminUsers /></ProtectedRoute>} />
              <Route path="/quote-portal/admin" element={<Navigate to="/quote-portal/admin/pricing" replace />} />
              {/* Convenience aliases */}
              <Route path="/quote-portal/pricing" element={<Navigate to="/quote-portal/admin/pricing" replace />} />
              <Route path="/quote-portal/users" element={<Navigate to="/quote-portal/admin/users" replace />} />
            </Route>

            {/* DEFAULTS */}
            <Route path="/" element={<Navigate to="/ramp-calculator" replace />} />
            <Route path="*" element={<Navigate to="/ramp-calculator" replace />} />
          </Routes>
        </ErrorBoundary>
        <Toaster position="top-right" />
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
